import { useStoreState } from '@ariakit/react'
import { omit } from 'lodash-es'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'

import { reportClientError } from '../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { DtcAddLogoFormValues, LogoFile } from '../../../../../containers/EmbroideryContainer/types'
import { useEmbroideryForm } from '../../../../../containers/EmbroideryContainer/useEmbroideryForm'
import useEmbroideryLogoUpload, {
  logoUploadErrorContext,
} from '../../../../../containers/EmbroideryContainer/useEmbroideryLogoUpload'
import {
  findDigitizationVariant,
  useEmbroideryVariantsQuery,
} from '../../../../../containers/EmbroideryContainer/useEmbroideryVariants'
import { formatPrettyCurrency } from '../../../../../lib/utils'
import { createSafeContainer } from '../../../../../lib/utils/safe-container'
import { LogoPreviewItem } from '../PreviewItems/LogoPreviewItem'

import { useAddLogoValidationSchema } from './useAddLogoValidationSchema'
import { useLogoSetupValidationSchema } from './useLogoSetupValidationSchema'

type LogoFormContainerState = {
  userLogos?: LogoFile[]
  transientLogo?: LogoFile
}

const useLogoFormContainer = ({ userLogos, transientLogo }: LogoFormContainerState) => {
  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const {
    setEmbroiderySelections,
    submittedEmbroiderySelections,
    setSelectedFormKind,
    selectedFormKind,
  } = useEmbroidery()

  // Determine the initial logo files that are available for the user.
  // This should include:
  // 1. Logo files that are saved in the user's account
  // 2. Digitization requests that exist in the cart
  // 3. Digitization requests that are confirmed, but not yet added to the cart
  const initialLogoFiles = useMemo(() => {
    const out: LogoFile[] = []

    // Add user logos to available logo files
    if (userLogos && userLogos.length > 0) {
      out.push(...userLogos)
    }

    // Add existing cart digitization requests to available logo files
    if (transientLogo) out.push(transientLogo)

    // Add uploaded logo to available logo files
    if (submittedEmbroiderySelections.LOGO_SETUP?.logoFile)
      out.push(submittedEmbroiderySelections.LOGO_SETUP.logoFile)

    // De-dupe & return existing logo files
    return out.reduce<LogoFile[]>((acc, curr) => {
      if (acc.find(logo => logo.id === curr.id)) {
        return acc
      }
      return [...acc, curr]
    }, [])
  }, [userLogos, transientLogo, submittedEmbroiderySelections.LOGO_SETUP?.logoFile])

  const [logoFiles, setLogoFiles] = useState<LogoFile[]>(initialLogoFiles)

  // Handle upload of user logos
  const upload = useEmbroideryLogoUpload()

  const captureError = useCallback((error: Error) => {
    reportClientError({
      error,
      context: {
        scope: 'DTCLogoEmbroideryForm useForm',
        category: 'embroidery',
        message: 'embroidery logo upload request failed',
        level: 'error',
        ...logoUploadErrorContext(error),
      },
    })
  }, [])

  /* Manage local form state for the LOGO_SETUP form */
  const logoSetupValidationSchema = useLogoSetupValidationSchema({ textMaxChars: 50 })
  const logoSetupForm = useEmbroideryForm('LOGO_SETUP', {
    validationSchema: logoSetupValidationSchema,
    finalFormKind: 'LOGO_DTC',
    async createEmbroideryOptions(values) {
      const existingLogoSetup = submittedEmbroiderySelections.LOGO_SETUP

      // Avoid uploading the same file during editing
      let data: LogoFile
      if (
        existingLogoSetup &&
        values.file === submittedEmbroiderySelections.LOGO_SETUP?.userSelectedFile
      ) {
        data = {
          ...submittedEmbroiderySelections.LOGO_SETUP?.logoFile!,
          digitizationSetting: values.digitization_setting!,
          name: values.name ?? '',
          notes: values.notes ?? '',
        }
      } else {
        data = await upload(values)
      }

      // Update existing logo file if it exists, otherwise push new logo file
      setLogoFiles(lf => {
        if (existingLogoSetup) {
          return lf.map(logo => (logo.id === existingLogoSetup.logoFile.id ? data : logo))
        }
        return [...lf, data]
      })

      return {
        userSelectedFile: values.file,
        setupFile: true,
        logoFile: data,
      }
    },
    onSubmitError: error => {
      captureError(error as Error)
      logoSetupForm.form.setError('file', fixtures.validationErrorMessages.fileUploadError)
    },
  })

  /* Manage local form state for the ADD_LOGO form */
  const addLogoForm = useEmbroideryForm('LOGO_DTC', {
    validationSchema: useAddLogoValidationSchema(),
    // Ensure that the proper logo file is initially selected based on the existing form state
    createInitialValues: initialValues => {
      const approvedLogo = logoFiles.find(lf => lf.status === 'APPROVED')
      const preferredInitialLogoFromId = logoFiles.find(
        file => file.id === initialValues.logoFileId
      )
      const preferredInitialLogo = initialValues.logoFile ?? preferredInitialLogoFromId
      const setupLogo =
        logoFiles.find(lf => lf.id === submittedEmbroiderySelections.LOGO_SETUP?.logoFile.id) ??
        transientLogo
      const selectedLogo = submittedEmbroiderySelections.LOGO_DTC?.logoFile

      const initialSelectedLogoFile =
        setupLogo ?? selectedLogo ?? preferredInitialLogo ?? approvedLogo

      return {
        ...initialValues,
        logoFile: initialSelectedLogoFile,
        logoFileId: initialSelectedLogoFile?.id,
      }
    },
    createEmbroideryOptions(values) {
      const logoFile = values.logoFile ?? submittedEmbroiderySelections.LOGO_SETUP?.logoFile
      return {
        logoPlacement: values.logoPlacement,
        logoFile: logoFile,
        logoId: logoFile?.id,
        logoPreview: logoFile?.preview,
        logoColor: logoFile?.digitizationSetting,
        logoNote: logoFile?.notes ?? '',
        logoName: logoFile?.name,
      }
    },
    previewItem: (values: DtcAddLogoFormValues) => <LogoPreviewItem values={values} />,
    onSubmitCompleted: values => {
      // Clear LOGO_SETUP on logo selection change to avoid duplicate fees.
      setEmbroiderySelections(prev =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        selectedFormKind === 'LOGO_DTC' && prev.LOGO_SETUP?.logoFile?.id !== values.logoFileId
          ? omit(prev, 'LOGO_SETUP')
          : prev
      )
    },
  })

  const addLogoValues = useStoreState(addLogoForm.form, s => s.values)

  const resetLogoForms = useCallback(() => {
    // Reset local & remote form states for LOGO_SETUP & ADD_LOGO
    addLogoForm.resetForm({ skipCreateInitialValues: true, clearSticky: true })
    logoSetupForm.resetForm()

    // Only remove logo files that are not saved or in the cart
    setLogoFiles(logoFiles =>
      logoFiles.filter(logo => {
        const isSaved = userLogos?.find(savedLogo => logo.id === savedLogo.id)
        const isTransient = logo.id === transientLogo?.id
        return isSaved || isTransient
      })
    )

    setSelectedFormKind('LOGO_SETUP')
  }, [addLogoForm, logoSetupForm, setSelectedFormKind, transientLogo, userLogos])

  const { data: embroideryVariantsQueryData } = useEmbroideryVariantsQuery()
  const embroideryVariants = useMemo(() => {
    return embroideryVariantsQueryData?.embroideryVariants ?? []
  }, [embroideryVariantsQueryData?.embroideryVariants])

  const logoSetupPrice = useMemo(() => {
    return submittedEmbroiderySelections.LOGO_SETUP
      ? findDigitizationVariant(embroideryVariants)?.productInfo.price
      : undefined
  }, [submittedEmbroiderySelections.LOGO_SETUP, embroideryVariants])

  // Price of the selected logo color SKU
  const logoPrice = useMemo(() => {
    const bw = embroideryVariants.find(embroideryVariant => embroideryVariant.key === 'LOGO_BW')
      ?.productInfo.price
    const color = embroideryVariants.find(
      embroideryVariant => embroideryVariant.key === 'LOGO_COLOR'
    )?.productInfo.price
    const selected = addLogoValues.logoFile?.digitizationSetting === 'BLACK_WHITE' ? bw : color
    return {
      color,
      bw,
      selected,
    }
  }, [addLogoValues.logoFile?.digitizationSetting, embroideryVariants])

  // Total price of the selected logo (color + setup)
  const logoTotalPrice = useMemo(() => {
    const price = logoPrice.selected
      ? {
          ...logoPrice.selected,
        }
      : undefined
    if (
      price?.amount &&
      logoSetupPrice?.amount &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      submittedEmbroiderySelections.LOGO_SETUP?.logoFile?.id === addLogoValues.logoFile?.id
    ) {
      price.amount += logoSetupPrice.amount
    }
    return price
  }, [
    logoPrice.selected,
    logoSetupPrice?.amount,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    submittedEmbroiderySelections.LOGO_SETUP?.logoFile?.id,
    addLogoValues.logoFile?.id,
  ])

  const { locale } = useLocalization()
  const logoDisplayPrices = useMemo(() => {
    return {
      total: logoTotalPrice
        ? formatPrettyCurrency({
            value: logoTotalPrice.amount,
            currency: logoTotalPrice.currencyCode,
            unit: 'dollars',
            locale,
          })
        : undefined,
      setup: logoSetupPrice
        ? formatPrettyCurrency({
            value: logoSetupPrice.amount,
            currency: logoSetupPrice.currencyCode,
            unit: 'dollars',
            locale,
          })
        : undefined,
      color: logoPrice.color
        ? formatPrettyCurrency({
            value: logoPrice.color.amount,
            currency: logoPrice.color.currencyCode,
            unit: 'dollars',
            locale,
          })
        : undefined,
      bw: logoPrice.bw
        ? formatPrettyCurrency({
            value: logoPrice.bw.amount,
            currency: logoPrice.bw.currencyCode,
            unit: 'dollars',
            locale,
          })
        : undefined,
      selected: logoPrice.selected
        ? formatPrettyCurrency({
            value: logoPrice.selected.amount,
            currency: logoPrice.selected.currencyCode,
            unit: 'dollars',
            locale,
          })
        : undefined,
    }
  }, [locale, logoPrice.bw, logoPrice.color, logoPrice.selected, logoSetupPrice, logoTotalPrice])

  // Ensure that the correct form kind is being displayed based on existing form state & available logos on mount
  // @TODO: Revisit this to avoid having to handle the initial form view after first render
  useLayoutEffect(() => {
    setSelectedFormKind(() => {
      if (submittedEmbroiderySelections.LOGO_DTC?.logoFile) {
        return 'LOGO_DTC'
      }
      if ((userLogos && userLogos.length > 0) || transientLogo) {
        return 'LOGO_DTC'
      }
      return 'LOGO_SETUP'
    })
    //eslint-disable-next-line
  }, [])

  const selectedLogo = addLogoValues.logoFile
  // Non-approved logos should still show as needing digitization, even if the setup line was removed.
  const selectedLogoNeedsDigitization = Boolean(selectedLogo && selectedLogo.status !== 'APPROVED')

  return {
    logoDisplayPrices,
    logoSetupForm: logoSetupForm.form,
    addLogoForm: addLogoForm.form,
    addLogoIsDirty: addLogoForm.isDirty,
    logoFiles,
    displayLogoSetup: selectedFormKind === 'LOGO_SETUP',
    includesDigitization:
      Boolean(submittedEmbroiderySelections.LOGO_SETUP) ||
      transientLogo?.id === selectedLogo?.id ||
      selectedLogoNeedsDigitization,
    resetLogoForms,
    transientLogo,
    displayLogoForm: (display: 'LOGO_SETUP' | 'LOGO_DTC') => setSelectedFormKind(display),
  }
}

export const LogoFormContainer = createSafeContainer(useLogoFormContainer)
